import React, { useState } from "react";
import { Alert, AlertTitle, CircularProgress, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import * as selectors from "selectors";
import { useClientTherapyHours, useUpdateClientTherapyHours } from "../../ClientAvailability/hooks";
import { useGlobalToast } from "components/GlobalToastProvider";
import SectionWrapper from "../../../Widgets/SectionWrapper";
import ViewFields from "./ViewFields";
import EditFields from "./EditFields";
import { TherapyHours } from "./interface";
import useGetClientInsurancePlanCptCodes from "hooks/api/insurance/useGetClientInsurancePlanCptCodes";

/**
 * Standalone PreliminaryTherapyHours component that manages its own state and data fetching.
 * This component handles the preliminary therapy hours recommendation section independently with its own edit mode.
 * Extracts clientId from URL parameters - no props required.
 */
const PreliminaryTherapyHours: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const { showToast } = useGlobalToast();
  const isCaregiver = useSelector(selectors.isCaregiver);
  const userPermissions = useSelector(selectors.getUserPermissionsList);
  const canEdit = !isCaregiver && Boolean(userPermissions?.edit_client_availability);

  const [editMode, setEditMode] = useState<boolean>(false);
  const [, setIsSaving] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [therapyHours, setTherapyHours] = useState<TherapyHours>({
    assessment_hours: 0,
    direct_therapy_hours: 0,
    caregiver_mediated_hours: 0,
    protocol_modification_hours: 0,
    indirect_protocol_modification_hours: 0,
    caregiver_training_hours: 0,
    protocol_modification_type: "",
  });
  const [originalTherapyHours, setOriginalTherapyHours] = useState<TherapyHours | null>(null);

  const { cptCodes, isLoading: cptCodesLoading } = useGetClientInsurancePlanCptCodes({ clientId });

  const { isLoading, refetch: refetchClientTherapyHours } = useClientTherapyHours({
    clientId,
    enabled: !!clientId,
    onSuccess: ({ data }) => {
      if (data?.hours) {
        setTherapyHours(data.hours);
        if (!editMode) {
          setOriginalTherapyHours(data.hours);
        }
      }
    },
  });

  const { mutate: saveTherapyHours } = useUpdateClientTherapyHours({
    onSuccess: () => {
      setIsSaving(false);
      setHasChanges(false);
      setEditMode(false);
      refetchClientTherapyHours();
      showToast({
        message: "Therapy hours updated successfully!",
        errorState: false,
      });
    },
    onError: () => {
      setIsSaving(false);
      showToast({
        message: "Whoops! Something went wrong",
        errorState: true,
      });
    },
  });

  const handleTherapyHoursChange = (field: keyof TherapyHours, value: number | string): void => {
    if (typeof value === "number" && value < 0) {
      return;
    }
    setTherapyHours((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasChanges(true);
  };

  const handleEdit = (): void => {
    setEditMode(true);
    setOriginalTherapyHours(therapyHours);
  };

  const handleCancel = (): void => {
    if (originalTherapyHours) {
      setTherapyHours(originalTherapyHours);
    }
    setEditMode(false);
    setHasChanges(false);
  };

  const handleSave = (): void => {
    if (!clientId) {
      showToast({
        message: "Client ID is missing",
        errorState: true,
      });
      return;
    }

    setIsSaving(true);
    saveTherapyHours({ newTherapyHours: therapyHours, clientId });
  };

  const renderContent = () => {
    // If no clientId, show error
    if (!clientId) {
      return (
        <Typography variant="h6" color="error">
          Client ID is required
        </Typography>
      );
    }

    if (isLoading || cptCodesLoading) {
      return <CircularProgress size={30} color="primary" style={{ marginBottom: 10 }} />;
    }

    return editMode ? (
      <EditFields
        therapyHours={therapyHours}
        onTherapyHoursChange={handleTherapyHoursChange}
        cptCodes={cptCodes}
      />
    ) : (
      <ViewFields therapyHours={therapyHours} cptCodes={cptCodes} />
    );
  };

  return (
    <SectionWrapper
      title="Recommended Hours"
      description="Configure the recommended therapy hours for each therapy service."
      editMode={editMode}
      onEdit={canEdit ? handleEdit : undefined}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={!hasChanges}
    >
      <Alert severity="info" variant="standard" sx={{ mb: 7 }}>
        <AlertTitle>Payor Requirements</AlertTitle>
        Each payor defines its own CPT-to-service mapping, determining which services are available
        for the client under that insurance plan. If no CPT code is mapped for a service, that
        service is hidden from the account.
      </Alert>
      {renderContent()}
    </SectionWrapper>
  );
};

export default PreliminaryTherapyHours;
export type { TherapyHours } from "./interface";
